import React, { useEffect, useReducer, useState } from "react";
import { useTranslation } from "react-i18next";
import { WTBGamePhase, initialWTBState, wtbReducer } from "./WhosTheBotReducer";
import { InGameViewProps } from "views/GamePage";
import RuleModal from "components/common/RuleModal";
import { WhosTheBotSocketService } from "views/wtb/WhosTheBotSocketService";
import UserSession from "network/UserSession";
import WhosTheBotRuleBody from "./WhosTheBotRuleBody";
import "sass/views/wtb/WhosTheBotView.scss";
import ProductGameHeader from "../ProductGameHeader";

const WhosTheBotView: React.FC<InGameViewProps> = ({
  product: recreation,
  room,
  messages,
  setMessages,
  onReturnToWaitingRoom,
}) => {
  const { t } = useTranslation();
  const [gameState, dispatch] = useReducer(wtbReducer, initialWTBState);
  const [answer, setAnswer] = useState("");
  const [isRuleOpen, setIsRuleOpen] = useState(false);
  const [isSettingOpen, setIsSettingOpen] = useState(false);
  const [showFinalResults, setShowFinalResults] = useState(false);
  const [socketService, setSocketService] =
    useState<WhosTheBotSocketService | null>(null);

  // 현재 플레이어 찾기
  // const currentPlayer = gameState.players.find((player) => player.isActive);

  useEffect(() => {
    const service = new WhosTheBotSocketService(dispatch);
    service.initializeListeners();
    setSocketService(service);

    return () => {
      service.cleanup();
    };
  }, [dispatch]);

  // 페이즈 변경 시 답변 입력 필드 초기화
  useEffect(() => {
    if (gameState.phase !== WTBGamePhase.WAITING) {
      setAnswer("");
    }
  }, [gameState.phase]);

  // Timer effect
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    const startTimer = () => {
      if (gameState.phase !== WTBGamePhase.WAITING && gameState.leftTime > 0) {
        intervalId = setInterval(() => {
          const currentTime = gameState.leftTime;
          if (currentTime > 0) {
            dispatch({ type: "DECREMENT_TIME" });
          } else {
            if (intervalId) {
              clearInterval(intervalId);
            }
          }
        }, 100);
      }
    };

    startTimer();

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [gameState.phase, gameState.leftTime]);

  // 타이머가 0이 되었을 때 처리
  // useEffect(() => {
  //   if (gameState.leftTime === 0 && gameState.phase !== WTBGamePhase.WAITING) {
  //     if (gameState.phase === WTBGamePhase.REVEAL) {
  //       dispatch({ type: "NEXT_PHASE" });
  //     } else {
  //       dispatch({ type: "NEXT_PHASE" });
  //     }
  //   }
  // }, [gameState.leftTime, gameState.phase, gameState.phase]);

  const handleSubmitAnswer = () => {
    const userId = UserSession.getInstance().getUserId();
    if (answer.trim().length > 0 && socketService && userId > -1) {
      socketService.submitAnswer(answer.trim(), userId);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmitAnswer();
    }
  };

  const handleVoteSelect = (answerId: number) => {
    if (gameState.phase === WTBGamePhase.VOTING && !gameState.isVoteConfirmed) {
      dispatch({ type: "SELECT_ANSWER", answerId });
      if (socketService) {
        socketService.submitVote(answerId);
      }
    }
  };

  const handleFinalVoteSelect = (playerId: number) => {
    if (gameState.phase === WTBGamePhase.AI_VOTING) {
      dispatch({ type: "SELECT_AI_VOTE", playerId });
      if (socketService) {
        socketService.submitFinalVote(playerId);
      }
    }
  };

  // AI VOTING 페이즈 종료 시 투표 결과 전송
  useEffect(() => {
    if (
      gameState.phase === WTBGamePhase.AI_VOTING &&
      gameState.leftTime === 0 &&
      gameState.selectedAIVote
    ) {
      if (socketService) {
        socketService.submitFinalVote(gameState.selectedAIVote);
      }
    }
  }, [
    gameState.phase,
    gameState.leftTime,
    gameState.selectedAIVote,
    socketService,
  ]);

  const getCurrentRoundAnswers = () => {
    return gameState.players
      .map((player) => ({
        playerId: player.id,
        text: player.answers[gameState.currentRound - 1] || "",
        isBot: player.isBot,
        votes: player.votes[gameState.currentRound - 1] || 0,
      }))
      .filter((answer) => answer.text !== null && answer.text !== "");
  };

  const renderHeader = () => (
    <ProductGameHeader
      product={recreation}
      room={room}
      round={gameState.currentRound}
      leftTime={Math.ceil(gameState.leftTime)}
      isRuleOpen={isRuleOpen}
      setIsRuleOpen={setIsRuleOpen}
      isSettingOpen={isSettingOpen}
      setIsSettingOpen={setIsSettingOpen}
      t={t}
    >
      {/* Leave Room button */}
      <button
        className="w-full px-4 py-2.5 bg-gray-800 hover:bg-red-600 text-red-400 hover:text-white transition-colors font-semibold flex items-center justify-center "
        onClick={() => onReturnToWaitingRoom()}
      >
        <i className="fas fa-sign-out-alt mr-2" />
        {t("leaveRoom")}
      </button>
    </ProductGameHeader>
  );

  const renderQuestion = () => {
    if (
      gameState.phase === WTBGamePhase.WAITING ||
      gameState.phase === WTBGamePhase.AI_VOTING ||
      gameState.phase === WTBGamePhase.FINAL
    ) {
      return null;
    }

    return (
      <div className="w-full max-w-3xl mx-auto mt-8 px-6">
        <div className="mb-8 p-2 bg-gray-800 rounded-xl border border-gray-700">
          <h2 className="text-xl text-center font-bold text-gray-100">
            {gameState.currentQuestion}
          </h2>
        </div>
      </div>
    );
  };

  const renderPlayerList = () => {
    const myId = UserSession.getInstance().getUserId();
    const sortedPlayers = [...gameState.players].sort((a, b) => {
      // 자신을 맨 앞으로
      if (a.id === myId) return -1;
      if (b.id === myId) return 1;
      return 0;
    });

    return (
      <div className="w-full px-2 mt-2 pb-4">
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 2xl:grid-cols-12 gap-1">
          {sortedPlayers.map((player) => {
            const isMe = player.id === myId;
            return (
              <div
                key={player.id}
                className={`relative flex items-center gap-1 p-1 rounded-lg transition-all ${
                  isMe
                    ? "bg-gradient-to-br from-indigo-600/20 to-blue-700/20 border border-indigo-500/50"
                    : "bg-gray-800/50 hover:bg-gray-700/50"
                }`}
              >
                <div className="relative flex-shrink-0">
                  <div
                    className={`w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center text-[10px] sm:text-xs rounded-full ${
                      isMe ? "text-indigo-300" : "text-gray-300"
                    }`}
                  >
                    {player.avatar}
                  </div>
                  {player.isActive && (
                    <div className="absolute bottom-0 right-0 w-1 h-1 sm:w-1.5 sm:h-1.5 bg-green-400 rounded-full border border-gray-800"></div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-0.5">
                    <span
                      className={`text-[10px] sm:text-xs font-medium truncate ${
                        isMe ? "text-indigo-300" : "text-gray-300"
                      }`}
                      title={player.name}
                    >
                      {player.name}
                    </span>
                    {isMe && (
                      <span className="px-0.5 py-[1px] bg-indigo-600 text-white rounded-full text-[8px] sm:text-[10px] flex-shrink-0">
                        {t("me")}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderAnswerPhase = () => {
    const currentPlayer = gameState.players.find(
      (player) => player.id === UserSession.getInstance().getUserId()
    );
    const hasSubmitted =
      currentPlayer?.answers[gameState.currentRound - 1] !== null;

    return (
      <div className="flex flex-col items-center mt-8">
        <div className="w-full max-w-lg px-6">
          <div className="relative">
            <textarea
              className={`w-full p-4 border-none rounded-lg shadow-md focus:ring-2 focus:ring-indigo-500 outline-none resize-none text-gray-100 bg-gray-800 placeholder-gray-500 ${
                hasSubmitted ? "opacity-75 cursor-not-allowed" : ""
              }`}
              placeholder={
                hasSubmitted ? t("WTB.answerSubmitted") : t("WTB.enterAnswer")
              }
              rows={3}
              maxLength={30}
              value={answer}
              onChange={(e) => !hasSubmitted && setAnswer(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={gameState.phase !== WTBGamePhase.ANSWER || hasSubmitted}
            />
            <div className="absolute bottom-2 right-3 text-sm text-gray-500">
              {answer.length}/30
            </div>
          </div>
          <button
            onClick={handleSubmitAnswer}
            disabled={
              answer.trim().length === 0 ||
              gameState.phase !== WTBGamePhase.ANSWER ||
              hasSubmitted
            }
            className={`mt-4 px-6 py-3 rounded-lg shadow-md transition-colors w-full font-medium ${
              hasSubmitted
                ? "bg-green-600 text-white cursor-not-allowed"
                : "bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            }`}
          >
            {hasSubmitted ? t("WTB.answerSubmitted") : t("WTB.submitAnswer")}
          </button>
        </div>
      </div>
    );
  };

  const renderAnswers = (
    isSelectable: boolean,
    title: string,
    subtitle?: string
  ) => {
    const myId = UserSession.getInstance().getUserId();
    const sortedAnswers = [...gameState.currentRoundAnswerIndices].sort(
      (a, b) => {
        // 자신의 답변을 맨 앞으로
        if (a === myId) return -1;
        if (b === myId) return 1;
        return 0;
      }
    );

    return (
      <div className="mt-4 px-4">
        <h3 className="text-lg font-medium text-center text-gray-100 mb-3">
          {title}
        </h3>
        {subtitle && (
          <div className="text-center text-sm text-gray-400 mb-4">
            {subtitle}
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {sortedAnswers.map((playerId, index) => {
            const player = gameState.players.find((p) => p.id === playerId);
            const answer = player?.answers[gameState.currentRound - 1];
            const isMyAnswer = playerId === myId;

            if (!answer) return null;

            return (
              <div
                key={`answer-${playerId}`}
                onClick={
                  isSelectable && !isMyAnswer
                    ? () => handleVoteSelect(playerId)
                    : undefined
                }
                className={`bg-gray-800 rounded-lg p-3 border relative overflow-hidden
                  ${
                    isSelectable && !isMyAnswer
                      ? "cursor-pointer hover:shadow-lg hover:translate-y-[-2px] transition-all border-gray-700"
                      : isMyAnswer
                      ? "border-indigo-500 bg-indigo-900/30"
                      : "border-gray-700"
                  }
                  ${
                    gameState.selectedAnswerId === playerId
                      ? "ring-2 ring-emerald-500 bg-emerald-900/30 border-emerald-500"
                      : ""
                  }`}
                style={{
                  animation: `fadeIn 0.5s ease-out ${index * 0.1}s both`,
                  opacity: 0,
                }}
              >
                <div className="flex items-center gap-2 mb-2">
                  {/* <div
                    className={`w-6 h-6 flex items-center justify-center text-sm rounded-full ${
                      isMyAnswer
                        ? "bg-gradient-to-br from-indigo-600 to-blue-700"
                        : "bg-gradient-to-br from-gray-700 to-gray-800"
                    }`}
                  >
                    {player?.avatar}
                  </div>
                  <span
                    className={`text-sm font-medium truncate ${
                      isMyAnswer ? "text-indigo-300" : "text-gray-300"
                    }`}
                  >
                    {player?.name}
                  </span> */}
                  {isMyAnswer && (
                    <span className="px-1.5 py-0.5 bg-indigo-600 text-white rounded-full text-xs">
                      {t("WTB.myAnswer")}
                    </span>
                  )}
                </div>
                <div className="text-gray-100 text-sm bg-gray-700 rounded-lg p-2 min-h-[40px]">
                  {answer}
                </div>
                {isSelectable &&
                  !isMyAnswer &&
                  gameState.selectedAnswerId === playerId && (
                    <div className="absolute top-2 right-2 px-2 py-1 bg-emerald-600 text-white rounded-full text-xs font-medium shadow-lg">
                      {t("WTB.selected")}
                    </div>
                  )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderResultsPhase = () => {
    const currentRoundResult =
      gameState.roundResults[gameState.currentRound - 1];
    if (!currentRoundResult) return null;

    return (
      <div className="mt-4 px-4">
        <h3 className="text-lg font-medium text-center text-gray-100 mb-4">
          {t("WTB.roundResults")} - {t("round")} {gameState.currentRound}
        </h3>

        {/* 상위 답변 섹션 */}
        <div className="mb-6">
          <h4 className="text-base font-medium text-center text-indigo-400 mb-3">
            {t("WTB.topAnswers")}
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {currentRoundResult.topAnswers.map((answer, index) => {
              // const player = gameState.players.find(
              //   (p) => p.id === answer.playerId
              // );
              // const votes = player?.votes[gameState.currentRound - 1] || 0;

              return (
                <div
                  key={answer.playerId}
                  className="bg-gray-800 rounded-lg p-3 border border-gray-700 relative overflow-hidden"
                >
                  {currentRoundResult.topAnswers.length > 0 && (
                    <>
                      <div className="absolute top-0 left-0 px-2 py-0.5 bg-indigo-600 text-white text-xs rounded-br-lg">
                        #{index + 1} {t("WTB.mostVoted")}
                      </div>
                      {/* <div className="absolute top-0 right-0 px-2 py-0.5 text-gray-400 text-xs">
                        {votes} {t("votes")}
                      </div> */}
                    </>
                  )}
                  <div className="mt-4">
                    <p className="text-sm text-gray-100">{answer.answer}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderAIVotingPhase = () => {
    const myId = UserSession.getInstance().getUserId();
    const currentPlayer = gameState.players.find(
      (player) => player.id === myId
    );
    const hasVoted = currentPlayer?.finalVote !== undefined;

    // 자신을 맨 앞으로 정렬
    const sortedPlayers = [...gameState.players].sort((a, b) => {
      if (a.id === myId) return -1;
      if (b.id === myId) return 1;
      return 0;
    });

    return (
      <div className="mt-8 px-6">
        <h3 className="text-2xl font-medium text-center text-gray-100 mb-8">
          {t("WTB.aiVotingTitle")}
        </h3>
        <div className="text-center text-gray-400 mb-8">
          {hasVoted ? t("WTB.voteSubmitted") : t("WTB.selectBot")}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sortedPlayers.map((player) => {
            const isMe = player.id === myId;
            const isSelected = gameState.selectedAIVote === player.id;
            return (
              <div
                key={player.id}
                onClick={() =>
                  !hasVoted && !isMe && handleFinalVoteSelect(player.id)
                }
                className={`bg-gray-800 rounded-lg p-6 border relative ${
                  hasVoted
                    ? "border-gray-700 cursor-not-allowed"
                    : isMe
                    ? "border-indigo-500 bg-indigo-900/30 cursor-not-allowed"
                    : "border-gray-700 hover:border-emerald-500 cursor-pointer hover:shadow-lg transition-all"
                } ${
                  isSelected
                    ? "ring-2 ring-emerald-500 bg-emerald-900/30 border-emerald-500"
                    : ""
                }`}
              >
                {isMe && (
                  <div className="absolute top-2 left-2 px-2 py-1 bg-indigo-600 text-white rounded-full text-xs font-medium">
                    {t("WTB.me")}
                  </div>
                )}
                <div className="flex items-center space-x-4 mb-4">
                  <div
                    className={`text-2xl p-2 rounded-full ${
                      isMe
                        ? "bg-gradient-to-br from-indigo-600 to-blue-700 border-2 border-indigo-400"
                        : isSelected
                        ? "bg-gradient-to-br from-emerald-600 to-emerald-700 border-2 border-emerald-400"
                        : "bg-gradient-to-br from-indigo-800 to-blue-900 border-2 border-gray-700"
                    }`}
                  >
                    {player.avatar}
                  </div>
                  <div
                    className={`text-lg font-medium ${
                      isMe
                        ? "text-indigo-300"
                        : isSelected
                        ? "text-emerald-300"
                        : "text-gray-100"
                    }`}
                  >
                    {player.name}
                  </div>
                </div>

                <div className="space-y-3">
                  {player.answers.map((answer, index) => (
                    <div
                      key={index}
                      className={`rounded-lg p-3 ${
                        isSelected
                          ? "bg-emerald-900/30 text-emerald-100"
                          : "bg-gray-700 text-gray-200"
                      }`}
                    >
                      <div className="text-sm text-gray-400 mb-1">
                        {t("round")} {index + 1}
                      </div>
                      <div>{answer || "-"}</div>
                    </div>
                  ))}
                </div>

                {isSelected && (
                  <div className="mt-4 text-center">
                    <span className="px-3 py-1 bg-emerald-600 text-white rounded-full text-sm font-medium shadow-lg">
                      {t("WTB.selected")}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderFinalResults = () => {
    if (!gameState.finalResults) return null;

    if (!showFinalResults) {
      return (
        <div className="mt-8 px-4">
          <h3 className="text-2xl font-medium text-center text-gray-100 mb-8">
            {t("WTB.finalResults")}
          </h3>

          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 mb-8">
            <div className="text-center text-xl text-indigo-400 mb-4">
              {t("WTB.botReveal")}
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              {gameState.finalResults.botPlayers.map((bot) => (
                <div
                  key={bot.id}
                  className="flex items-center space-x-3 bg-gray-700 px-4 py-2 rounded-lg"
                >
                  <span className="text-xl">{bot.avatar}</span>
                  <span className="text-gray-100">{bot.name}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700 mb-8">
            <div className="text-center text-xl text-indigo-400 mb-4">
              {t("WTB.correctGuessers")}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {gameState.players
                .filter((player) =>
                  gameState.finalResults?.correctGuesserIds.includes(player.id)
                )
                .map((player) => (
                  <div
                    key={player.id}
                    className="flex items-center space-x-3 bg-gray-700 px-4 py-2 rounded-lg"
                  >
                    <span className="text-xl">{player.avatar}</span>
                    <span className="text-gray-100">{player.name}</span>
                  </div>
                ))}
            </div>
          </div>

          <div className="flex justify-center">
            <button
              onClick={() => setShowFinalResults(true)}
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              {t("WTB.continue")}
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="mt-8 px-4 pb-20">
        <h3 className="text-2xl font-medium text-center text-gray-100 mb-8">
          {t("WTB.gameResults")}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {gameState.players
            .sort((a, b) => b.score - a.score)
            .map((player) => (
              <div
                key={player.id}
                className="bg-gray-800 rounded-lg p-4 border border-gray-700"
              >
                <div className="flex items-center space-x-3">
                  <div
                    className={`w-10 h-10 flex items-center justify-center text-lg rounded-full ${
                      player.id === UserSession.getInstance().getUserId()
                        ? "bg-gradient-to-br from-indigo-600 to-blue-700"
                        : "bg-gradient-to-br from-gray-700 to-gray-800"
                    }`}
                  >
                    {player.avatar}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-gray-100 font-medium ${
                          player.id === UserSession.getInstance().getUserId()
                            ? "text-indigo-300"
                            : ""
                        }`}
                      >
                        {player.name}
                      </span>
                      {player.id === UserSession.getInstance().getUserId() && (
                        <span className="px-1.5 py-0.5 bg-indigo-600 text-white rounded-full text-xs">
                          {t("me")}
                        </span>
                      )}
                    </div>
                    <div className="text-indigo-400 text-sm mt-1">
                      {player.score} {t("points")}
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>
        <div
          className="absolute cursor-pointer left-[50%] transform -translate-x-1/2 px-8 flex justify-center items-center m-auto bottom-4 py-2 text-center text-medium text-white bg-gray-800 hover:bg-gray-700 rounded-xl border border-gray-700 transition-colors"
          onClick={onReturnToWaitingRoom}
        >
          <i className="fas fa-sign-out-alt mr-2" />
          {t("backToRoom")}
        </div>
      </div>
    );
  };

  const renderGameContent = () => {
    switch (gameState.phase) {
      case WTBGamePhase.WAITING:
        return null;
      case WTBGamePhase.ANSWER:
        return renderAnswerPhase();
      case WTBGamePhase.VOTING:
        return renderAnswers(true, t("WTB.votePrompt"), t("WTB.selectAnswer"));
      case WTBGamePhase.RESULTS:
        return renderResultsPhase();
      case WTBGamePhase.AI_VOTING:
        return renderAIVotingPhase();
      case WTBGamePhase.FINAL:
        return renderFinalResults();
      default:
        return null;
    }
  };

  return (
    <div className="space-gray-theme wtb-container flex flex-col min-h-screen">
      {renderHeader()}
      {isRuleOpen && (
        <div className="relative z-50">
          {/* RuleModal은 ProductGameHeader 내부에서 처리됨 */}
        </div>
      )}
      <div className="flex-1 overflow-y-auto pt-[56px]">
        {gameState.phase !== WTBGamePhase.WAITING && renderQuestion()}
        {renderGameContent()}
      </div>
      {gameState.phase !== WTBGamePhase.AI_VOTING &&
        gameState.phase !== WTBGamePhase.FINAL && (
          <div className="mt-auto border-t border-gray-700 bg-gray-800/50">
            {renderPlayerList()}
          </div>
        )}
    </div>
  );
};

export default WhosTheBotView;
